import { useRef, useState } from 'react'
import { ChartJSOrUndefined } from 'react-chartjs-2/dist/types'
import { NavLink } from 'react-router-dom'
import { Button } from '../../../components/button'
import { ChartCard } from '../../../components/cards/ChartCard'
import DoughnutChart, {
  CustomLegend,
} from '../../../components/charts/DoughnutChart'
import Icon from '../../../components/icon'
import Dropdown from '../../../components/inputs/dropdown'
import { SearchInput } from '../../../components/inputs/searchInput'
import { ExportDataModal } from '../../../components/modal/app_modals/exportDataModal'
import { TableComponent } from '../../../components/table'
import {
  formatCurrency,
  getPageFromSkip,
  getSkipFromPage,
} from '../../../helpers'
import { useAppSelector } from '../../../hooks'
import { useModalControl } from '../../../hooks/useModalControl'
import {
  useExportCreatorsReportMutation,
  useGetCreatorsReportQuery,
} from '../../../redux-toolkits/reports/reports.slice'
import { DashboardReportParams } from '../../../redux-toolkits/reports/reports.type'
import { ReportQueryProps } from '../../../redux-toolkits/reports/reports.type'
export const CreatorsReportView = ({
  mainDashQuery,
}: {
  mainDashQuery: DashboardReportParams
}) => {
  const [query, setQuery] = useState<ReportQueryProps>({
    filter: 'today',
    isCustomDateRange: false,
    startDate: undefined,
    endDate: undefined,
    skip: 0,
    limit: 20,
    sort: 'desc',
  })

  const { selectedFulfilmentCenters } = useAppSelector((state) => state.auth)

  const { modal, modalOpen, setModalOpen, handleModalClose, handleModalOpen } =
    useModalControl()

  const { data: respData, isFetching: fetchingData } =
    useGetCreatorsReportQuery(
      {
        ...query,
        ...mainDashQuery,
        search: query.search === '' ? undefined : query.search,
        fulfilmentCentre:
          selectedFulfilmentCenters.map((c) => c._id).join(',') || undefined,
      },
      {
        refetchOnMountOrArgChange: true,
      },
    )

  const [exportReport, { isLoading: exportingReport }] =
    useExportCreatorsReportMutation()

  const chartRef = useRef<ChartJSOrUndefined<'doughnut'>>(null)

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ChartCard title="Active Creators">
          <div className="flex gap-4 items-center flex-wrap">
            <div className="mt-4 w-[214px] h-[214px] relative">
              <p className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-bold">
                  {respData?.data?.totalCreators ?? 0}
                </span>
                <span className="text-sec-black text-sm">total creators</span>
              </p>
              <DoughnutChart
                labels={['Active Creators', 'Inactive Creators']}
                bgColor={['#1B9066', '#CA3542']}
                data={[
                  respData?.data?.activeCreatorsCount ?? 0,
                  (respData?.data?.totalCreators ?? 0) -
                    (respData?.data?.activeCreatorsCount ?? 0),
                ]}
                height={50}
                width={50}
                responsive
                hoverLabel="Count"
                chartRef={chartRef}
                customLegend={true}
              />
            </div>

            <div className="flex-1 gap-4 flex flex-col">
              <p className="flex items-center gap-2">
                <Icon
                  id="storefront"
                  width={24}
                  height={24}
                  className="text-[#1B9066]"
                />
                <span className="text-xs text-sec-black">
                  {(respData?.data?.activeCreatorsCount ?? 0).toLocaleString()}{' '}
                  creator(s) are active
                </span>
              </p>

              <p className="flex items-center gap-2">
                <Icon
                  id="storefront"
                  width={24}
                  height={24}
                  className="text-[#CA3542]"
                />
                <span className="text-xs text-sec-black">
                  {(
                    (respData?.data?.totalCreators ?? 0) -
                    (respData?.data?.activeCreatorsCount ?? 0)
                  ).toLocaleString()}{' '}
                  creator(s) are inactive
                </span>
              </p>
            </div>
          </div>

          <CustomLegend chartRef={chartRef} className="mt-10" />
        </ChartCard>
      </div>

      {/* <CountCardContainer className="!my-0">
        <CountCard
          count={respData?.data?.activeCreatorsCount}
          text="Active Creators"
          icon=""
          isCurrency={false}
          loading={loadingData}
          valueSuffix={`/ ${respData?.data?.totalCreators ?? 0}`}
        />
      </CountCardContainer> */}

      <div className="flex-wrap flex items-center gap-4 justify-between">
        <div className="flex-1 min-w-full md:min-w-fit">
          <SearchInput
            placeholder="Search"
            searchFunction={(val) => {
              setQuery({
                ...query,
                search: val,
              })
            }}
            className="!w-full md:!max-w-[400px]"
          />
        </div>

        <Button
          label="Export"
          onClick={async () => {
            handleModalOpen('exportDataModal')
          }}
          loading={exportingReport}
          primary
          className="w-full max-w-[129px]"
        />

        <Dropdown
          className="!max-h-[58px]"
          menuButton={
            <button type="button" className="rotate-90">
              <Icon width={58} height={56} id="verticalDots" />
            </button>
          }
          menuItems={[
            {
              name: 'Total Sales: low to high',
              value: 'asc',
              onClick: () => {
                setQuery({
                  ...query,
                  sort: 'asc',
                })
              },
            },
            {
              name: 'Total Sales: high to low',
              value: 'desc',
              onClick: () => {
                setQuery({
                  ...query,
                  sort: 'desc',
                })
              },
            },
          ]}
          menuItemClassName="border-b-0"
          menuClassName="!w-[200px]"
        />
      </div>

      <div className="w-full overflow-x-scroll">
        <TableComponent
          headers={[
            'CREATOR',
            'NO OF ORDERS',
            'TOTAL SALES (₦)',
            'AVERAGE ORDER VALUE (₦)',
            'AMOUNT SETTLED (₦)',
          ]}
          rows={
            respData?.data?.result?.map((summary, idx) => {
              return {
                id: idx,
                content: [
                  <NavLink
                    to={`/creators/${summary._id}`}
                    className={`flex gap-2 items-center`}
                    key={`product-${summary._id}-name`}
                  >
                    {summary.businessName}
                  </NavLink>,
                  summary.orderCount.toLocaleString(),
                  formatCurrency(summary.totalSales),
                  formatCurrency(summary.averageOrderValue),
                  formatCurrency(summary.amountSettled),
                ],
              }
            }) ?? []
          }
          isEmpty={[0, undefined].includes(respData?.data?.result?.length)}
          loading={fetchingData}
          currentPage={getPageFromSkip(query.skip!, query.limit!)}
          totalDataCount={respData?.data?.count}
          pageLimit={Number(query.limit!)}
          onPageChange={(page) => {
            setQuery({
              ...query,
              skip: getSkipFromPage(page, query.limit!),
            })
          }}
        />
      </div>

      <ExportDataModal
        open={modalOpen && modal === 'exportDataModal'}
        setOpen={setModalOpen}
        onCloseCallback={() => {
          handleModalClose()
        }}
        onSuccessCallback={() => {
          handleModalClose()
        }}
        extraValues={{}}
        title={`Export Creators`}
        reportType="creators"
      />
    </>
  )
}
